// Hash routing (decisions.md PWA-24): every screen has a URL (#/route/trail/tmb, #/more/pro…), the static host and
// the service worker only ever serve index.html, and Stripe/restore links can land on a precise screen.

import { signal } from '@preact/signals';

export interface Route {
  /** Path segments, decoded: "#/route/trail/tmb" → ["route", "trail", "tmb"]. */
  path: string[];
  query: URLSearchParams;
}

export type Tab = 'map' | 'route' | 'tonight' | 'trip' | 'more';
export const tabs: readonly Tab[] = ['map', 'route', 'tonight', 'trip', 'more'];

export function parseHash(hash: string): Route {
  const raw = hash.replace(/^#\/?/, '');
  const q = raw.indexOf('?');
  const pathPart = q >= 0 ? raw.slice(0, q) : raw;
  const query = new URLSearchParams(q >= 0 ? raw.slice(q + 1) : '');
  const path = pathPart
    .split('/')
    .filter(Boolean)
    .map((s) => {
      try {
        return decodeURIComponent(s);
      } catch {
        return s;
      }
    });
  return { path, query };
}

export const route = signal<Route>(parseHash(typeof location === 'undefined' ? '' : location.hash));

if (typeof window !== 'undefined') {
  window.addEventListener('hashchange', () => {
    route.value = parseHash(location.hash);
  });
}

/** "#/a/b?x=1" for segments and an optional query. */
export function href(path: string | string[], query?: Record<string, string | undefined>): string {
  const segments = (Array.isArray(path) ? path : path.split('/')).filter(Boolean).map(encodeURIComponent);
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query ?? {})) if (v !== undefined) params.set(k, v);
  const qs = params.toString();
  return `#/${segments.join('/')}${qs ? `?${qs}` : ''}`;
}

export function navigate(path: string | string[], options: { replace?: boolean; query?: Record<string, string | undefined> } = {}) {
  const target = href(path, options.query);
  if (options.replace) {
    history.replaceState(history.state, '', target);
    route.value = parseHash(target);
  } else if (location.hash !== target) {
    location.hash = target;
  }
}

export function currentTab(r: Route): Tab | undefined {
  const first = r.path[0];
  return (tabs as readonly string[]).includes(first ?? '') ? (first as Tab) : undefined;
}
